import api from '../api/api';

const halaqaService = {
  /**
   * Get all halaqas (for admin use)
   * @returns {Promise} Promise resolving to list of all halaqas
   */
  getAllHalaqas: async () => {
    try {
      const response = await api.get('/halaqas');
      
      // Check for standard backend response structure
      if (response.data && response.data.success) {
        return response.data.data || []; // Return data array or empty array
      }
      
      // Fallback if response doesn't match expected structure
      return response.data || [];
    } catch (error) {
      console.error('Error fetching all halaqas:', error);
      throw error;
    }
  },
  /**
   * Get halaqas for the current teacher
   * @returns {Promise} Promise resolving to list of assigned halaqas
   */
  getAssignedHalaqas: async () => {
    try {
      const response = await api.get('/halaqas');
      
      // Check for standard backend response structure
      if (response.data && response.data.success) {
        return response.data.data || []; // Return data array or empty array
      }
      
      // Fallback if response doesn't match expected structure
      return response.data || [];
    } catch (error) {
      console.error('Error fetching assigned halaqas:', error);
      
      // Detailed error handling
      if (error.response) {
        if (error.response.status === 403) {
          throw new Error('Not authorized to view halaqas');
        } else if (error.response.status === 404) {
          throw new Error('No halaqas found for this teacher');
        } else {
          throw new Error(`Server error: ${error.response.status}`);
        }
      } else if (error.request) {
        throw new Error('No response received from server');
      } else {
        throw new Error('Error setting up halaqa request');
      }
    }
  },

  /**
   * Get students for a specific halaqa
   * @param {string} halaqaId - ID of the halaqa
   * @returns {Promise} Promise resolving to list of students in the halaqa
   */
  getHalaqaStudents: async (halaqaId) => {
    try {
      const response = await api.get(`/halaqas/${halaqaId}/students`);
      
      // Check for standard backend response structure
      if (response.data && response.data.success) {
        return response.data.data || [];
      }
      
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching students for halaqa ${halaqaId}:`, error);
      
      if (error.response) {
        if (error.response.status === 403) {
          throw new Error('Not authorized to view halaqa students');
        } else if (error.response.status === 404) {
          throw new Error('Halaqa not found');
        }
      }
      throw error;
    }
  },

  /**
   * Get teachers assigned to a specific halaqa
   * @param {string} halaqaId - ID of the halaqa
   * @returns {Promise} Promise resolving to list of teachers assigned to the halaqa
   */
  getHalaqaTeachers: async (halaqaId) => {
    try {
      const response = await api.get('/users', {
        params: {
          role: 'teacher'
        }
      });
      
      // Fallback: Get teachers directly from the halaqa document
      const halaqaResponse = await api.get(`/halaqas/${halaqaId}`);
      const halaqaData = halaqaResponse.data?.data;
      
      if (halaqaData && Array.isArray(halaqaData.teachers)) {
        console.log('Using halaqa.teachers[]:', halaqaData.teachers);
        return halaqaData.teachers;
      }
      
      console.warn('No teachers found in halaqa.teachers[]');
      return [];
    } catch (error) {
      console.error(`Error fetching teachers for halaqa ${halaqaId}:`, error);
      return [];
    }
  },

  /**
   * Get all message recipients for the current teacher
   * Includes students in assigned halaqas
   * @returns {Promise} Promise resolving to list of recipients
   */
  getTeacherMessageRecipients: async () => {
    try {
      // Get all halaqas assigned to the teacher
      const halaqas = await halaqaService.getAssignedHalaqas();

      // Get all students from each halaqa
      const studentsPromises = halaqas.map(halaqa =>
        halaqaService.getHalaqaStudents(halaqa._id)
      );

      const studentsArrays = await Promise.all(studentsPromises);

      // Flatten and dedupe students
      const allStudents = [];
      const studentIds = new Set();

      studentsArrays.forEach(students => {
        students.forEach(student => {
          if (!studentIds.has(student._id)) {
            studentIds.add(student._id);
            allStudents.push(student);
          }
        });
      });

      // Fetch parents of those students
      let parentRecipients = [];
      const studentIdList = Array.from(studentIds);

      if (studentIdList.length > 0) {
        try {
          console.log('Fetching parents for student IDs:', studentIdList);
          const response = await api.get('/users', {
            params: {
              role: 'parent',
              'parentInfo.children': studentIdList.join(','),
            },
          });
          
          if (Array.isArray(response.data)) {
            const parentIds = new Set();

            parentRecipients = response.data
              .filter(parent => {
                if (!parent._id) {
                  console.warn('Skipping parent with no _id:', parent);
                  return false;
                }
                if (parentIds.has(parent._id.toString())) {
                  return false;
                }
                parentIds.add(parent._id.toString());
                return true;
              })
              .map(parent => ({
                ...parent,
                recipientType: 'parent',
              }));
          }
        } catch (err) {
          console.warn('Could not fetch parent recipients:', err);
        }
      }

      // Final recipients: students + their parents
      const recipients = [
        ...allStudents.map(student => ({
          ...student,
          recipientType: 'student',
        })),
        ...parentRecipients,
      ];
      
      return recipients;
    } catch (error) {
      console.error('Error fetching teacher message recipients:', error);
      return [];
    }
  },

  /**
   * Get teachers available for messaging by a student
   * @returns {Promise} Promise resolving to list of teachers
   */
  getStudentTeachers: async () => {
    try {
      // Use the dedicated message recipients endpoint
      const response = await api.get('/messages/recipients');
      
      if (response.data && response.data.success) {
        return response.data.data || [];
      } else if (Array.isArray(response.data)) {
        return response.data;
      }
      
      console.warn('No teachers found');
      return [];
    } catch (error) {
      console.error('Error fetching student teachers:', error);
      return [];
    }
  },

  /**
   * Get teachers available for messaging by a parent
   * These are teachers of the parent's children
   * @returns {Promise} Promise resolving to list of teachers
   */
  getParentMessageRecipients: async () => {
    try {
      // Use the dedicated message recipients endpoint
      const response = await api.get('/messages/recipients');
      
      if (response.data && response.data.success) {
        return response.data.data || [];
      } else if (Array.isArray(response.data)) {
        return response.data;
      }
      
      console.warn('No message recipients found');
      return [];
    } catch (error) {
      console.error('Error fetching parent message recipients:', error);
      return [];
    }
  },
  
  /**
   * Get all halaqas available for group messaging by the current user
   * @returns {Promise} Promise resolving to list of halaqas
   */
  getMessageableHalaqas: async () => {
    try {
      // For teachers, this is just their assigned halaqas
      const halaqas = await halaqaService.getAssignedHalaqas();
      return halaqas;
    } catch (error) {
      console.error('Error fetching messageable halaqas:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        throw new Error('No response received from server');
      } else {
        throw new Error('Error setting up halaqas request');
      }
    }
  },
  
  /**
   * For admin use only: get all users in the system
   * @returns {Promise} Promise resolving to list of all users
   */
  getAllUsers: async () => {
    try {
      const response = await api.get('/users');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching all users:', error);
      
      if (error.response) {
        throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        throw new Error('No response received from server');
      } else {
        throw new Error('Error setting up users request');
      }
    }
  }
};

export default halaqaService;